import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getReceiptById, getReceipts, updateReceipt, deleteReceipt } from '../../services/receiptManagementService';
import { CATEGORY_OPTIONS, getCategoryDisplayName, normalizeCategoryId } from '../../lib/categories';
import {
  getCountryCurrencySymbol,
  getSelectedCountry,
  normalizeCurrencySymbol,
  normalizeVatDataForCountry
} from '../../lib/receiptCountryUtils';
import { getCountryTerminology } from '../../lib/countrySettings';
import { getTransactionVatAmount } from '../../lib/vatReportingUtils';

/**
 * Format a category code into a user-friendly name
 *
 * @param {string} category - The category code
 * @returns {string} The formatted category name
 */
const formatCategoryName = (category) => getCategoryDisplayName(normalizeCategoryId(category));

const toEditableNumberString = (value) => (
  value !== undefined && value !== null && value !== '' ? value.toString() : ''
);

const buildEditableLineItems = (items = [], fallbackCategory = 'supplies') => (
  Array.isArray(items)
    ? items.map((item, index) => ({
        ...item,
        description: item?.description || `Item ${index + 1}`,
        quantity: toEditableNumberString(item?.quantity ?? 1),
        unitPrice: toEditableNumberString(item?.unitPrice),
        amount: toEditableNumberString(item?.amount ?? item?.total ?? item?.unitPrice),
        category: normalizeCategoryId(item?.category || fallbackCategory || 'supplies'),
        saveToCost: item?.saveToCost === true
      }))
    : []
);

const buildEditableData = (data = {}) => ({
  ...data,
  amount: toEditableNumberString(data.amount),
  vatAmount: toEditableNumberString(data.vatAmount),
  vatRate: toEditableNumberString(data.vatRate),
  items: buildEditableLineItems(data.items, data.category)
});

const parseAmountValue = (value) => {
  if (value === undefined || value === null) return null;

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const raw = value.toString().trim();
  if (raw === '') return null;

  let normalized = raw.replace(/[^\d,.-]/g, '');
  if (!normalized) return null;

  const hasComma = normalized.includes(',');
  const hasDot = normalized.includes('.');

  if (hasComma && hasDot) {
    if (normalized.lastIndexOf(',') > normalized.lastIndexOf('.')) {
      normalized = normalized.replace(/\./g, '').replace(',', '.');
    } else {
      normalized = normalized.replace(/,/g, '');
    }
  } else if (hasComma) {
    const parts = normalized.split(',');
    normalized = parts.length === 2 && parts[1].length <= 2
      ? `${parts[0]}.${parts[1]}`
      : normalized.replace(/,/g, '');
  }

  const parsed = parseFloat(normalized);
  return Number.isNaN(parsed) ? null : parsed;
};

const parseVatRateValue = (value) => {
  const parsed = parseAmountValue(value);
  if (parsed === null || parsed < 0) return null;
  return parsed > 1 ? parsed / 100 : parsed;
};

const roundToTwoDecimals = (value) => parseFloat(value.toFixed(2));

const calculateItemsTotal = (items = []) => {
  let hasAnyAmount = false;
  const total = (Array.isArray(items) ? items : []).reduce((sum, item) => {
    let itemAmount = parseAmountValue(item?.amount);
    if (itemAmount === null) {
      const itemQty = parseAmountValue(item?.quantity);
      const itemUnitPrice = parseAmountValue(item?.unitPrice);
      if (itemQty !== null && itemUnitPrice !== null) {
        itemAmount = itemQty * itemUnitPrice;
      } else if (itemUnitPrice !== null) {
        itemAmount = itemUnitPrice;
      }
    }

    if (itemAmount === null) {
      return sum;
    }

    hasAnyAmount = true;
    return sum + itemAmount;
  }, 0);

  return hasAnyAmount ? roundToTwoDecimals(total) : null;
};

const normalizeReceiptExtractedData = (data = {}) => {
  const normalized = { ...data };

  const amountValue = parseAmountValue(normalized.amount ?? normalized.total);
  normalized.amount = amountValue === null ? null : parseFloat(amountValue.toFixed(2));

  let vatRateValue = parseVatRateValue(normalized.vatRate);
  let vatAmountValue = parseAmountValue(normalized.vatAmount);
  if (vatAmountValue !== null) {
    vatAmountValue = parseFloat(vatAmountValue.toFixed(2));
  }

  let vatWasCorrected = false;
  if (normalized.amount !== null && normalized.amount > 0) {
    if (vatAmountValue !== null && vatAmountValue > normalized.amount) {
      vatWasCorrected = true;
      vatAmountValue = null;
    }
  }

  normalized.vatRate = vatRateValue;
  normalized.vatAmount = vatAmountValue;

  let normalizedItems = Array.isArray(normalized.items)
    ? normalized.items.map((item, index) => {
        const itemAmountRaw = parseAmountValue(item?.amount ?? item?.total);
        const itemUnitPriceRaw = parseAmountValue(item?.unitPrice);
        const parsedQuantity = parseAmountValue(item?.quantity);

        let itemAmount = itemAmountRaw;
        let itemUnitPrice = itemUnitPriceRaw;

        if (itemAmount === null && itemUnitPrice !== null && parsedQuantity !== null && parsedQuantity !== 0) {
          itemAmount = itemUnitPrice * parsedQuantity;
        } else if (itemAmount === null && itemUnitPrice !== null) {
          itemAmount = itemUnitPrice;
        }

        if (itemUnitPrice === null && itemAmount !== null && parsedQuantity !== null && parsedQuantity !== 0) {
          itemUnitPrice = itemAmount / parsedQuantity;
        }

        return {
          ...item,
          description: item?.description || `Item ${index + 1}`,
          amount: itemAmount === null ? null : parseFloat(itemAmount.toFixed(2)),
          quantity: parsedQuantity === null ? (item?.quantity ?? 1) : parsedQuantity,
          unitPrice: itemUnitPrice === null ? null : parseFloat(itemUnitPrice.toFixed(2)),
          category: normalizeCategoryId(item?.category || normalized.category || 'supplies'),
          saveToCost: item?.saveToCost === true
        };
      })
        .filter((item) => item.description || item.amount !== null || item.unitPrice !== null)
    : [];

  const totalItemsAmount = normalizedItems.reduce((sum, item) => sum + (item.amount || 0), 0);
  if (
    normalized.amount !== null &&
    normalized.amount > 0 &&
    normalizedItems.length === 1 &&
    totalItemsAmount > normalized.amount * 5
  ) {
    const existingItem = normalizedItems[0] || {};
    normalizedItems = [{
      ...existingItem,
      description: existingItem.description || `${normalized.vendor || 'Receipt'} purchase`,
      quantity: 1,
      unitPrice: normalized.amount,
      amount: normalized.amount
    }];
  }

  normalized.items = normalizedItems;

  if ((normalized.amount === null || normalized.amount === 0) && normalizedItems.length > 0) {
    const fallbackAmount = calculateItemsTotal(normalizedItems);
    if (fallbackAmount !== null) {
      normalized.amount = fallbackAmount;
    }
  }

  if (vatWasCorrected && normalized.vatRate !== null && normalized.vatAmount !== null) {
    normalized.vatComponents = [{
      rate: normalized.vatRate,
      amount: normalized.vatAmount,
      description: `${(normalized.vatRate * 100).toFixed(1)}% VAT`
    }];
  } else if (!Array.isArray(normalized.vatComponents)) {
    normalized.vatComponents = [];
  }

  return normalized;
};

/**
 * Receipt Detail Component
 * 
 * This component displays the details of a receipt and allows editing and conversion to a transaction.
 */
function ReceiptDetail({ activeWorkspace = null, transactions = [] }) {
  // Get the receipt ID from the URL
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // State
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [receiptOrder, setReceiptOrder] = useState([]);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [imageZoom, setImageZoom] = useState(1);
  const defaultCurrencySymbol = useMemo(() => getCountryCurrencySymbol(getSelectedCountry()), []);
  const taxName = useMemo(
    () => getCountryTerminology(receipt?.extractedData?.countryId || getSelectedCountry().id).taxName,
    [receipt?.extractedData?.countryId]
  );
  const linkedTransaction = useMemo(
    () => (Array.isArray(transactions)
      ? transactions.find((transaction) => String(transaction?.receiptId || '') === String(id || ''))
      : null),
    [id, transactions]
  );

  const goToReceiptList = (state = {}) => {
    navigate('/receipts', { state });
  };

  const routeReceiptIds = useMemo(
    () => (Array.isArray(location.state?.receiptIds) ? location.state.receiptIds.filter(Boolean) : []),
    [location.state]
  );
  const currentReceiptIndex = useMemo(() => receiptOrder.findIndex((receiptId) => receiptId === id), [id, receiptOrder]);
  const previousReceiptId = currentReceiptIndex > 0 ? receiptOrder[currentReceiptIndex - 1] : null;
  const nextReceiptId =
    currentReceiptIndex >= 0 && currentReceiptIndex < receiptOrder.length - 1
      ? receiptOrder[currentReceiptIndex + 1]
      : null;

  const navigateToReceipt = (targetId) => {
    if (!targetId) return;
    navigate(`/receipts/${targetId}`, {
      state: {
        receiptIds: receiptOrder
      }
    });
  };
  
  // Load receipt on mount
  useEffect(() => {
    const loadReceipt = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await getReceiptById(id, { workspace: activeWorkspace });
        console.log('Loaded receipt data:', data);

        // Ensure the receipt data is properly formatted
        if (data && data.extractedData) {
          // Format date if it exists
          if (data.extractedData.date) {
            try {
              const dateObj = new Date(data.extractedData.date);
              if (!isNaN(dateObj.getTime())) {
                data.extractedData.date = dateObj.toISOString().split('T')[0];
              }
            } catch (error) {
              console.warn('Invalid date format in receipt data:', error);
            }
          }

          // Ensure amount is a number
          if (data.extractedData.amount !== undefined && data.extractedData.amount !== null && data.extractedData.amount !== '') {
            data.extractedData.amount = parseAmountValue(data.extractedData.amount) || 0;
          } else if (data.extractedData.total) {
            // Some receipts might have 'total' instead of 'amount'
            data.extractedData.amount = parseAmountValue(data.extractedData.total) || 0;
          }

          // Ensure category is set
          if (!data.extractedData.category) {
            // Try to determine category from vendor or items
            const vendorText = data.extractedData.vendor ? data.extractedData.vendor.toLowerCase() : '';
            if (vendorText.includes('fuel') || vendorText.includes('petrol') || vendorText.includes('gas')) {
              data.extractedData.category = 'utilities';
            } else if (data.extractedData.items && data.extractedData.items.length > 0) {
              // Check if any items contain keywords related to categories
              const itemDescriptions = data.extractedData.items.map(item =>
                item.description ? item.description.toLowerCase() : '').join(' ');

              if (itemDescriptions.includes('diesel') || itemDescriptions.includes('petrol') ||
                  itemDescriptions.includes('fuel') || itemDescriptions.includes('gas')) {
                data.extractedData.category = 'utilities';
              } else if (itemDescriptions.includes('hair') || itemDescriptions.includes('salon')) {
                data.extractedData.category = 'hairServices';
              } else if (itemDescriptions.includes('product') || itemDescriptions.includes('supply')) {
                data.extractedData.category = 'supplies';
              }
            } else {
              data.extractedData.category = 'supplies'; // Default category
            }
          }

          // Ensure vendor is set
          if (!data.extractedData.vendor) {
            // Try to extract vendor from items if available
            if (data.extractedData.items && data.extractedData.items.length > 0 &&
                data.extractedData.items[0].description) {
              // Use the first item description as vendor if nothing else is available
              data.extractedData.vendor = data.extractedData.items[0].description;
            } else {
              data.extractedData.vendor = 'Unknown Vendor';
            }
          }

          // Ensure currency is set
          if (!data.extractedData.currency) {
            data.extractedData.currency = defaultCurrencySymbol;
          } else {
            data.extractedData.currency = normalizeCurrencySymbol(data.extractedData.currency, data.extractedData.countryId);
          }

          console.log('Formatted receipt data:', data.extractedData);
        }

        let normalizedExtractedData = data.extractedData
          ? normalizeVatDataForCountry(
              normalizeReceiptExtractedData({
                ...data.extractedData,
                category: normalizeCategoryId(data.extractedData.category),
                currency: normalizeCurrencySymbol(data.extractedData.currency, data.extractedData.countryId) || defaultCurrencySymbol
              }),
              {
                country: data.extractedData.countryId || getSelectedCountry().id,
                text: data.text || ''
              }
            )
          : {};

        if (linkedTransaction) {
          const transactionVatAmount = Number(getTransactionVatAmount(linkedTransaction) || linkedTransaction.vatAmount || 0);
          const receiptVatAmount = Number(normalizedExtractedData.vatAmount || 0);
          if (Number.isFinite(transactionVatAmount) && transactionVatAmount > 0 && transactionVatAmount >= receiptVatAmount) {
            const rawRate = Number(linkedTransaction.vatRate);
            const transactionVatRate = Number.isFinite(rawRate)
              ? (rawRate > 1 ? rawRate / 100 : rawRate)
              : (Number(linkedTransaction.amount) > transactionVatAmount
                  ? transactionVatAmount / (Number(linkedTransaction.amount) - transactionVatAmount)
                  : normalizedExtractedData.vatRate);
            const transactionVatComponents = Array.isArray(linkedTransaction.vatComponents) && linkedTransaction.vatComponents.length > 0
              ? linkedTransaction.vatComponents
              : [{
                  category: linkedTransaction.category || normalizedExtractedData.category || '',
                  rate: transactionVatRate,
                  amount: parseFloat(transactionVatAmount.toFixed(2)),
                  grossAmount: Number(linkedTransaction.amount) || Number(normalizedExtractedData.amount) || 0,
                  description: `${(transactionVatRate * 100).toFixed(1)}% ${taxName}`
                }];

            normalizedExtractedData = {
              ...normalizedExtractedData,
              amount: Number(linkedTransaction.amount) || normalizedExtractedData.amount,
              vendor: linkedTransaction.description || normalizedExtractedData.vendor,
              category: normalizeCategoryId(linkedTransaction.category || normalizedExtractedData.category || 'supplies'),
              vatAmount: parseFloat(transactionVatAmount.toFixed(2)),
              vatRate: transactionVatRate,
              vatComponents: transactionVatComponents,
              items: Array.isArray(normalizedExtractedData.items) && normalizedExtractedData.items.length > 0
                ? normalizedExtractedData.items.map((item, index) => ({
                    ...item,
                    vatAmount: index === 0 ? parseFloat(transactionVatAmount.toFixed(2)) : 0,
                    vatRate: index === 0 ? transactionVatRate : 0
                  }))
                : normalizedExtractedData.items
            };
          }
        }

        setReceipt({
          ...data,
          extractedData: normalizedExtractedData
        });

        // Initialize edited data with the receipt's extracted data
        setEditedData(buildEditableData(normalizedExtractedData));
      } catch (error) {
        console.error('Error loading receipt:', error);
        setError('Failed to load receipt. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadReceipt();
    }
  }, [activeWorkspace, defaultCurrencySymbol, id, linkedTransaction, taxName]);

  useEffect(() => {
    if (routeReceiptIds.length > 0) {
      setReceiptOrder(routeReceiptIds);
    }

    let isMounted = true;

    const loadReceiptOrder = async () => {
      try {
        const ids = [];
        const limit = 100;
        let offset = 0;
        let total = 0;

        do {
          const result = await getReceipts({}, limit, offset, { workspace: activeWorkspace });
          const batchIds = (result.receipts || []).map((receiptItem) => receiptItem.id).filter(Boolean);
          ids.push(...batchIds);
          total = result.total || ids.length;
          offset += limit;
          if (batchIds.length === 0) break;
        } while (offset < total && offset < 5000);

        if (isMounted) {
          setReceiptOrder(ids);
        }
      } catch (orderError) {
        console.warn('Could not load receipt order for navigation:', orderError);
      }
    };

    loadReceiptOrder();

    return () => {
      isMounted = false;
    };
  }, [activeWorkspace, routeReceiptIds]);
  
  /**
   * Handle input change in edit mode
  */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === 'category' ? normalizeCategoryId(value) : value;

    setEditedData(prev => ({
      ...prev,
      [name]: nextValue
    }));
  };

  /**
   * Handle amount change (keep raw value for editing)
   */
  const handleAmountChange = (e) => {
    const { value } = e.target;

    setEditedData(prev => ({
      ...prev,
      amount: value.replace(',', '.')
    }));
  };

  /**
   * Handle date change (ensure proper format)
   */
  const handleDateChange = (e) => {
    const { value } = e.target;

    // Ensure date is in YYYY-MM-DD format
    let formattedDate = value;

    // If it's a valid date, store it
    if (formattedDate) {
      try {
        const dateObj = new Date(formattedDate);
        if (!isNaN(dateObj.getTime())) {
          // Valid date, format as YYYY-MM-DD
          formattedDate = dateObj.toISOString().split('T')[0];
        }
      } catch (error) {
        console.warn('Invalid date format:', error);
      }
    }

    setEditedData(prev => ({
      ...prev,
      date: formattedDate
    }));
  };

  const handleLineItemFieldChange = (itemIndex, field, value) => {
    setEditedData((prev) => {
      const updatedItems = Array.isArray(prev.items) ? [...prev.items] : [];
      const currentItem = updatedItems[itemIndex] || {};
      const nextItem = {
        ...currentItem,
        [field]: field === 'category' ? normalizeCategoryId(value) : value
      };

      if (field === 'quantity') {
        const qtyValue = parseAmountValue(value);
        const itemAmount = parseAmountValue(currentItem.amount);
        const itemUnitPrice = parseAmountValue(currentItem.unitPrice);
        if (qtyValue !== null && qtyValue > 0) {
          if (itemAmount !== null) {
            nextItem.unitPrice = toEditableNumberString(roundToTwoDecimals(itemAmount / qtyValue));
          } else if (itemUnitPrice !== null) {
            nextItem.amount = toEditableNumberString(roundToTwoDecimals(itemUnitPrice * qtyValue));
          }
        }
      }

      if (field === 'unitPrice') {
        const unitPriceValue = parseAmountValue(value);
        const qtyValue = parseAmountValue(currentItem.quantity);
        if (unitPriceValue !== null) {
          const lineAmount =
            qtyValue !== null && qtyValue > 0
              ? roundToTwoDecimals(unitPriceValue * qtyValue)
              : roundToTwoDecimals(unitPriceValue);
          nextItem.amount = toEditableNumberString(lineAmount);
        }
      }

      if (field === 'amount') {
        const amountValue = parseAmountValue(value);
        const qtyValue = parseAmountValue(currentItem.quantity);
        if (amountValue !== null && qtyValue !== null && qtyValue > 0) {
          nextItem.unitPrice = toEditableNumberString(roundToTwoDecimals(amountValue / qtyValue));
        }
      }

      updatedItems[itemIndex] = nextItem;

      const updatedTotal = calculateItemsTotal(updatedItems);
      return {
        ...prev,
        items: updatedItems,
        amount: updatedTotal === null ? prev.amount : updatedTotal.toFixed(2)
      };
    });
  };

  const handleAddLineItem = () => {
    setEditedData((prev) => ({
      ...prev,
      items: [
        ...(Array.isArray(prev.items) ? prev.items : []),
        {
          description: '',
          quantity: '1',
          unitPrice: '',
          amount: '',
          category: normalizeCategoryId(prev.category || receipt?.extractedData?.category || 'supplies'),
          saveToCost: false
        }
      ]
    }));
  };

  const handleRemoveLineItem = (itemIndex) => {
    setEditedData((prev) => {
      const updatedItems = (Array.isArray(prev.items) ? prev.items : [])
        .filter((_, index) => index !== itemIndex);
      const updatedTotal = calculateItemsTotal(updatedItems);

      return {
        ...prev,
        items: updatedItems,
        amount: updatedTotal === null ? prev.amount : updatedTotal.toFixed(2)
      };
    });
  };

  const handleSplitLineItem = (itemIndex) => {
    setEditedData((prev) => {
      const sourceItems = Array.isArray(prev.items) ? [...prev.items] : [];
      const sourceItem = sourceItems[itemIndex];
      if (!sourceItem) {
        return prev;
      }

      const sourceAmount = parseAmountValue(sourceItem.amount);
      const sourceQty = parseAmountValue(sourceItem.quantity);

      const firstItem = { ...sourceItem };
      const secondItem = {
        ...sourceItem,
        description: sourceItem.description ? `${sourceItem.description} (split)` : `Item ${itemIndex + 2}`
      };

      if (sourceAmount !== null) {
        const firstAmount = roundToTwoDecimals(sourceAmount / 2);
        const secondAmount = roundToTwoDecimals(sourceAmount - firstAmount);
        firstItem.amount = toEditableNumberString(firstAmount);
        secondItem.amount = toEditableNumberString(secondAmount);
      }

      if (sourceQty !== null && sourceQty > 0) {
        const firstQty = roundToTwoDecimals(sourceQty / 2);
        const secondQty = roundToTwoDecimals(sourceQty - firstQty);
        firstItem.quantity = toEditableNumberString(firstQty);
        secondItem.quantity = toEditableNumberString(secondQty);

        const firstAmount = parseAmountValue(firstItem.amount);
        const secondAmount = parseAmountValue(secondItem.amount);
        if (firstAmount !== null && firstQty > 0) {
          firstItem.unitPrice = toEditableNumberString(roundToTwoDecimals(firstAmount / firstQty));
        }
        if (secondAmount !== null && secondQty > 0) {
          secondItem.unitPrice = toEditableNumberString(roundToTwoDecimals(secondAmount / secondQty));
        }
      }

      sourceItems.splice(itemIndex, 1, firstItem, secondItem);
      const updatedTotal = calculateItemsTotal(sourceItems);

      return {
        ...prev,
        items: sourceItems,
        amount: updatedTotal === null ? prev.amount : updatedTotal.toFixed(2)
      };
    });
  };
  
  /**
   * Save edited data
   */
  const handleSave = async (afterSave = 'list') => {
    try {
      setLoading(true);

      // Format the data before saving
      const formattedData = {
        ...editedData
      };

      // Ensure date is in YYYY-MM-DD format
      if (formattedData.date) {
        try {
          const dateObj = new Date(formattedData.date);
          if (!isNaN(dateObj.getTime())) {
            formattedData.date = dateObj.toISOString().split('T')[0];
          }
        } catch (error) {
          console.warn('Invalid date format:', error);
        }
      }

      const normalizedExtractedData = normalizeReceiptExtractedData({
        ...formattedData,
        category: normalizeCategoryId(formattedData.category || receipt?.extractedData?.category || 'supplies')
      });

      // Update the receipt
      const updatedReceipt = await updateReceipt(id, {
        extractedData: normalizedExtractedData
      }, { workspace: activeWorkspace });

      const normalizedUpdatedExtractedData = normalizeReceiptExtractedData({
        ...(updatedReceipt.extractedData || normalizedExtractedData),
        category: normalizeCategoryId(
          updatedReceipt.extractedData?.category || normalizedExtractedData.category || 'supplies'
        )
      });

      // Keep local state consistent in case navigation is interrupted.
      setReceipt({
        ...updatedReceipt,
        extractedData: normalizedUpdatedExtractedData
      });
      setEditedData(buildEditableData(normalizedUpdatedExtractedData));
      setIsEditing(false);
      setIsImageModalOpen(false);
      setImageZoom(1);

      if (afterSave === 'previous' && previousReceiptId) {
        navigate(`/receipts/${previousReceiptId}`, {
          state: {
            receiptIds: receiptOrder
          }
        });
        return;
      }

      if (afterSave === 'next' && nextReceiptId) {
        navigate(`/receipts/${nextReceiptId}`, {
          state: {
            receiptIds: receiptOrder
          }
        });
        return;
      }

      // Default behavior keeps previous workflow: return to receipt list.
      goToReceiptList({ updatedReceiptId: id, saveSuccess: true });
    } catch (error) {
      console.error('Error updating receipt:', error);
      alert('Failed to update receipt. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Cancel editing
   */
  const handleCancel = () => {
    // Reset edited data to the receipt's extracted data
    setEditedData(buildEditableData(receipt.extractedData || {}));
    
    // Exit edit mode
    setIsEditing(false);
  };
  
  /**
   * Delete receipt
   */
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this receipt?')) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Delete the receipt
      await deleteReceipt(id, { workspace: activeWorkspace });

      const navigationOrder = (receiptOrder.length > 0 ? receiptOrder : routeReceiptIds)
        .filter(Boolean);
      const currentIndexInOrder = navigationOrder.findIndex((receiptId) => receiptId === id);
      const remainingOrder = navigationOrder.filter((receiptId) => receiptId !== id);

      const candidateNextId =
        currentIndexInOrder >= 0 && currentIndexInOrder < navigationOrder.length - 1
          ? navigationOrder[currentIndexInOrder + 1]
          : null;
      const candidatePreviousId =
        currentIndexInOrder > 0
          ? navigationOrder[currentIndexInOrder - 1]
          : null;

      const targetReceiptId =
        candidateNextId ||
        candidatePreviousId ||
        nextReceiptId ||
        previousReceiptId ||
        remainingOrder[0] ||
        null;

      if (targetReceiptId) {
        navigate(`/receipts/${targetReceiptId}`, {
          state: {
            receiptIds: remainingOrder.length > 0 ? remainingOrder : receiptOrder.filter((receiptId) => receiptId !== id),
            deletedReceiptId: id,
            deleteSuccess: true
          }
        });
        return;
      }

      goToReceiptList({ deletedReceiptId: id, deleteSuccess: true });
    } catch (error) {
      console.error('Error deleting receipt:', error);
      alert('Failed to delete receipt. Please try again.');
      setLoading(false);
    }
  };
  
  // Loading state
  if (loading && !receipt) {
    return (
      <div className="flex justify-center items-center py-8">
        <svg className="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-md">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium text-red-800">{error}</p>
          </div>
        </div>
      </div>
    );
  }
  
  // No receipt found
  if (!receipt) {
    return (
      <div className="bg-yellow-50 p-4 rounded-md">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium text-yellow-800">Receipt not found</p>
          </div>
        </div>
      </div>
    );
  }

  const lineItems = isEditing
    ? (Array.isArray(editedData.items) ? editedData.items : [])
    : (Array.isArray(receipt.extractedData?.items) ? receipt.extractedData.items : []);
  
  return (
    <div className="receipt-detail bg-white rounded-lg shadow p-4 sm:p-6 overflow-hidden">
      <div className="mb-6 space-y-3">
        <h2 className="text-xl font-semibold">Receipt Details</h2>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => goToReceiptList()}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 whitespace-nowrap"
            disabled={loading}
          >
            All Receipts
          </button>
          <button
            onClick={() => navigateToReceipt(previousReceiptId)}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            disabled={loading || !previousReceiptId}
          >
            Previous
          </button>
          <button
            onClick={() => navigateToReceipt(nextReceiptId)}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            disabled={loading || !nextReceiptId}
          >
            Next
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {!isEditing ? (
            <>
              <button
                onClick={() => setIsEditing(true)}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 whitespace-nowrap"
                disabled={loading}
              >
                Edit
              </button>
              
              <button
                onClick={handleDelete}
                className="px-3 py-1 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50 whitespace-nowrap"
                disabled={loading}
              >
                Delete
              </button>
              
            </>
          ) : (
            <>
              <button
                onClick={() => handleSave('previous')}
                className="px-3 py-1 border border-indigo-300 rounded-md text-xs sm:text-sm font-medium text-indigo-700 bg-white hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                disabled={loading || !previousReceiptId}
              >
                Save + Previous
              </button>

              <button
                onClick={() => handleSave('next')}
                className="px-3 py-1 border border-indigo-300 rounded-md text-xs sm:text-sm font-medium text-indigo-700 bg-white hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                disabled={loading || !nextReceiptId}
              >
                Save + Next
              </button>

              <button
                onClick={() => handleSave('list')}
                className="px-3 py-1 border border-indigo-500 rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 whitespace-nowrap"
                disabled={loading}
              >
                Save
              </button>
              
              <button
                onClick={handleCancel}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 whitespace-nowrap"
                disabled={loading}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Receipt Image */}
        <div className="receipt-image min-w-0">
          {receipt.imageUrl ? (
            <>
              <button
                type="button"
                className="w-full block"
                onClick={() => {
                  setImageZoom(1);
                  setIsImageModalOpen(true);
                }}
              >
                <img
                  src={receipt.imageUrl}
                  alt="Receipt"
                  className="w-full h-auto max-h-[70vh] sm:max-h-96 object-contain border rounded"
                />
              </button>
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setImageZoom(1);
                    setIsImageModalOpen(true);
                  }}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 whitespace-nowrap"
                >
                  Zoom Image
                </button>
                <a
                  href={receipt.imageUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 whitespace-nowrap"
                >
                  Open Full Image
                </a>
              </div>
            </>
          ) : (
            <div className="w-full h-48 bg-gray-200 flex items-center justify-center rounded">
              <span className="text-gray-500">Receipt image not available</span>
            </div>
          )}
          
          <div className="mt-2 text-sm text-gray-500 break-all">
            {receipt.filename || 'No filename available'}
          </div>
        </div>
        
        {/* Receipt Data */}
        <div className="receipt-data min-w-0">
          <h3 className="text-lg font-medium mb-3">Receipt Information</h3>
          
          <div className="space-y-4">
            {/* Date Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date
              </label>
              {isEditing ? (
                <input
                  type="date"
                  name="date"
                  value={editedData.date || ''}
                  onChange={handleDateChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              ) : (
                <div className="px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                  {receipt.extractedData?.date ? new Date(receipt.extractedData.date).toLocaleDateString() : 'N/A'}
                </div>
              )}
            </div>
            
            {/* Amount Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount
              </label>
              {isEditing ? (
                <input
                  type="text"
                  inputMode="decimal"
                  pattern="[-0-9.,]*"
                  name="amount"
                  value={editedData.amount ?? ''}
                  onChange={handleAmountChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="0.00"
                />
              ) : (
                <div className="px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                  {receipt.extractedData?.amount ?
                    `${receipt.extractedData.currency || defaultCurrencySymbol}${typeof receipt.extractedData.amount === 'number' ?
                      receipt.extractedData.amount.toFixed(2) :
                      parseFloat(receipt.extractedData.amount).toFixed(2)}` :
                    'N/A'}
                </div>
              )}
            </div>
            
            {/* Vendor Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vendor/Merchant
              </label>
              {isEditing ? (
                <input
                  type="text"
                  name="vendor"
                  value={editedData.vendor || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Vendor name"
                />
              ) : (
                <div className="px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                  {receipt.extractedData?.vendor || 'Unknown Vendor'}
                </div>
              )}
            </div>
            
            {/* Category Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              {isEditing ? (
                <select
                  name="category"
                  value={editedData.category || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Select a category</option>
                  {(CATEGORY_OPTIONS.expenses || []).map(({ id, name }) => (
                    <option key={id} value={id}>{name}</option>
                  ))}
                </select>
              ) : (
                <div className="px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                  {formatCategoryName(receipt.extractedData?.category) || 'Uncategorized'}
                </div>
              )}
            </div>
            
            {/* VAT Information */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {taxName} Information
              </label>
              {isEditing ? (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{taxName} Rate (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      name="vatRate"
                      value={editedData.vatRate || ''}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="23"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{taxName} Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      name="vatAmount"
                      value={editedData.vatAmount || ''}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              ) : (
                <div className="px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                  {receipt.extractedData?.vatRate !== null ?
                    `Rate: ${receipt.extractedData.vatRate === 0 ? '0' : 
                      (typeof receipt.extractedData.vatRate === 'number' && receipt.extractedData.vatRate < 1 ?
                      (receipt.extractedData.vatRate * 100).toFixed(0) : receipt.extractedData.vatRate)}% | ` : ''}
                  {receipt.extractedData?.vatAmount !== null ?
                    `Amount: ${receipt.extractedData.currency || defaultCurrencySymbol}${receipt.extractedData.vatAmount === 0 ? '0.00' : 
                      (typeof receipt.extractedData.vatAmount === 'number' ?
                      receipt.extractedData.vatAmount.toFixed(2) :
                      parseFloat(receipt.extractedData.vatAmount).toFixed(2))}` :
                    `No ${taxName} information available`}
                </div>
              )}
            </div>

            {/* Notes Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              {isEditing ? (
                <textarea
                  name="notes"
                  value={editedData.notes || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  rows="3"
                  placeholder="Additional notes"
                ></textarea>
              ) : (
                <div className="px-3 py-2 border border-gray-300 rounded-md bg-gray-50 min-h-[4rem]">
                  {receipt.extractedData?.notes || 'No notes'}
                </div>
              )}
            </div>

            {/* Receipt Items */}
            <div className="mt-4">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Receipt Items
                </label>
                {isEditing && (
                  <button
                    type="button"
                    onClick={handleAddLineItem}
                    className="px-2.5 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded hover:bg-indigo-100"
                  >
                    + Add line item
                  </button>
                )}
              </div>

              {lineItems.length === 0 ? (
                <div className="border border-gray-300 rounded-md bg-gray-50 p-4 text-sm text-gray-500">
                  No line items found in this receipt.
                </div>
              ) : (
                <div className="border border-gray-300 rounded-md bg-gray-50 overflow-x-auto">
                  <table className="w-full min-w-[640px] divide-y divide-gray-200">
                    <thead className="bg-gray-100">
                      <tr>
                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Qty
                        </th>
                        <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Price
                        </th>
                        <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total
                        </th>
                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Category
                        </th>
                        <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Save?
                        </th>
                        {isEditing && (
                          <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {lineItems.map((item, index) => (
                        <tr key={`${item.description || 'item'}-${index}`}>
                          <td className="px-3 py-2 text-sm text-gray-900 break-words">
                            {isEditing ? (
                              <input
                                type="text"
                                value={item.description || ''}
                                onChange={(e) => handleLineItemFieldChange(index, 'description', e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded-md"
                                placeholder={`Item ${index + 1}`}
                              />
                            ) : (
                              item.description || `Item ${index + 1}`
                            )}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 text-right">
                            {isEditing ? (
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.quantity ?? ''}
                                onChange={(e) => handleLineItemFieldChange(index, 'quantity', e.target.value)}
                                className="w-20 px-2 py-1 border border-gray-300 rounded-md text-right"
                                placeholder="1"
                              />
                            ) : (
                              item.quantity ?? '1'
                            )}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 text-right">
                            {isEditing ? (
                              <input
                                type="number"
                                min="-999999"
                                step="0.01"
                                value={item.unitPrice ?? ''}
                                onChange={(e) => handleLineItemFieldChange(index, 'unitPrice', e.target.value)}
                                className="w-24 px-2 py-1 border border-gray-300 rounded-md text-right"
                                placeholder="0.00"
                              />
                            ) : (
                              parseAmountValue(item.unitPrice) !== null
                                ? `${receipt.extractedData?.currency || defaultCurrencySymbol}${parseAmountValue(item.unitPrice).toFixed(2)}`
                                : 'N/A'
                            )}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 text-right">
                            {isEditing ? (
                              <input
                                type="number"
                                min="-999999"
                                step="0.01"
                                value={item.amount ?? ''}
                                onChange={(e) => handleLineItemFieldChange(index, 'amount', e.target.value)}
                                className="w-24 px-2 py-1 border border-gray-300 rounded-md text-right"
                                placeholder="0.00"
                              />
                            ) : (
                              parseAmountValue(item.amount) !== null
                                ? `${receipt.extractedData?.currency || defaultCurrencySymbol}${parseAmountValue(item.amount).toFixed(2)}`
                                : 'N/A'
                            )}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900">
                            {isEditing ? (
                              <select
                                value={normalizeCategoryId(item.category || editedData.category || 'supplies')}
                                onChange={(e) => handleLineItemFieldChange(index, 'category', e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded-md"
                              >
                                {(CATEGORY_OPTIONS.expenses || []).map(({ id: categoryId, name }) => (
                                  <option key={categoryId} value={categoryId}>{name}</option>
                                ))}
                              </select>
                            ) : (
                              formatCategoryName(item.category || receipt.extractedData?.category)
                            )}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 text-center">
                            {isEditing ? (
                              <input
                                type="checkbox"
                                checked={!!item.saveToCost}
                                onChange={(e) => handleLineItemFieldChange(index, 'saveToCost', e.target.checked)}
                              />
                            ) : (
                              <span className={`inline-flex items-center justify-center min-w-[44px] px-2 py-0.5 rounded-full text-[11px] font-medium ${item.saveToCost ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                {item.saveToCost ? 'Yes' : 'No'}
                              </span>
                            )}
                          </td>
                          {isEditing && (
                            <td className="px-3 py-2 text-sm text-right">
                              <div className="inline-flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleSplitLineItem(index)}
                                  className="px-2 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded hover:bg-indigo-100"
                                >
                                  Split
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveLineItem(index)}
                                  className="px-2 py-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100"
                                >
                                  Remove
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
          
          {/* Tax information - only show if explicitly provided */}
          {(receipt.extractedData?.vatAmount !== null || receipt.extractedData?.vatRate !== null) && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-700 mb-2">{taxName} Information</h4>
              <div className="bg-gray-50 p-3 rounded-md">
                {receipt.extractedData?.vatRate !== null && (
                  <div className="mb-1">
                    <strong className="text-sm">{taxName} Rate:</strong>
                    <span className="ml-2">
                      {receipt.extractedData.vatRate === 0 
                        ? '0' 
                        : (typeof receipt.extractedData.vatRate === 'number' && receipt.extractedData.vatRate < 1 
                          ? (receipt.extractedData.vatRate * 100).toFixed(0) 
                          : receipt.extractedData.vatRate)}%
                    </span>
                  </div>
                )}
                {receipt.extractedData?.vatAmount !== null && (
                  <div>
                    <strong className="text-sm">{taxName} Amount:</strong>
                    <span className="ml-2">
                      {receipt.extractedData.vatAmount === 0 
                        ? `${receipt.extractedData.currency || defaultCurrencySymbol}0.00` 
                        : `${receipt.extractedData.currency || defaultCurrencySymbol}${parseFloat(receipt.extractedData.vatAmount).toFixed(2)}`}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Receipt Metadata */}
          <div className="mt-6">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Receipt Metadata</h4>
            <div className="bg-gray-50 p-3 rounded-md text-xs text-gray-600 break-all">
              <div><strong>ID:</strong> {receipt.id}</div>
              <div><strong>Created:</strong> {receipt.createdAt ? new Date(receipt.createdAt).toLocaleString() : 'N/A'}</div>
              <div><strong>File Size:</strong> {receipt.fileSize ? `${Math.round(receipt.fileSize / 1024)} KB` : 'N/A'}</div>
              <div><strong>Content Type:</strong> {receipt.contentType || 'N/A'}</div>
            </div>
          </div>
        </div>
      </div>

      {isImageModalOpen && receipt.imageUrl && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-80 flex flex-col">
          <div className="flex flex-wrap items-center justify-between gap-2 p-3 bg-black bg-opacity-70">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setImageZoom((prev) => Math.max(1, parseFloat((prev - 0.25).toFixed(2))))}
                className="px-3 py-1 rounded-md bg-white text-gray-800 text-sm font-medium"
              >
                -
              </button>
              <button
                type="button"
                onClick={() => setImageZoom((prev) => Math.min(4, parseFloat((prev + 0.25).toFixed(2))))}
                className="px-3 py-1 rounded-md bg-white text-gray-800 text-sm font-medium"
              >
                +
              </button>
              <button
                type="button"
                onClick={() => setImageZoom(1)}
                className="px-3 py-1 rounded-md bg-white text-gray-800 text-sm font-medium"
              >
                Reset
              </button>
            </div>
            <button
              type="button"
              onClick={() => setIsImageModalOpen(false)}
              className="px-3 py-1 rounded-md bg-white text-gray-800 text-sm font-medium"
            >
              Close
            </button>
          </div>
          <div className="flex-1 overflow-auto p-4">
            <div className="min-h-full flex items-start justify-center">
              <img
                src={receipt.imageUrl}
                alt="Receipt zoomed"
                className="max-w-none"
                style={{
                  transform: `scale(${imageZoom})`,
                  transformOrigin: 'top center'
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReceiptDetail;
